import React, { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { companyInfo } from "../data/mockData";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  MessageSquare,
  User,
  BookOpen,
  Globe,
  CheckCircle,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";

const FadeIn = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const ContactPage = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        access_key: "39df39c5-e158-4f1c-a3e3-59ddd0b661a2",
        subject: `New Contact Inquiry from ${formData.name} — Sun Lumia Overseas`,
        from_name: "Sun Lumia Overseas Website",
        name: formData.name,
        email: formData.email,
        phone: formData.phone || "Not provided",
        preferred_country: formData.country || "Not specified",
        message: formData.message,
        replyto: formData.email,
      };

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitted(true);
        toast({
          title: "Message Sent Successfully!",
          description: "Thank you for reaching out. We'll get back to you within 24 hours.",
        });
        setTimeout(() => {
          setFormData({ name: "", email: "", phone: "", country: "", message: "" });
          setSubmitted(false);
        }, 5000);
      } else {
        throw new Error(result.message || "Something went wrong");
      }
    } catch (error) {
      toast({
        title: "Failed to send message",
        description: "Please try again or contact us directly via phone/email.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactCards = [
    {
      icon: Phone,
      title: "Call Us",
      details: [companyInfo.phone, companyInfo.altPhone],
      action: `tel:${companyInfo.phone.replace(/\s/g, "")}`,
    },
    {
      icon: Mail,
      title: "Email Us",
      details: [companyInfo.email],
      action: `mailto:${companyInfo.email}`,
    },
    {
      icon: MapPin,
      title: "Visit Us",
      details: [
        "Q-4, 3rd Floor, Suman Tower",
        "Nr Hotel Haveli, Sector-11",
        "Gandhinagar - 382011, Gujarat",
      ],
      action: "https://maps.google.com/?q=Gandhinagar+Gujarat+India",
    },
    {
      icon: Clock,
      title: "Business Hours",
      details: ["Mon - Sat: 10:00 AM - 6:00 PM", "Sunday: Closed"],
      action: null,
    },
  ];

  return (
    <div>
      {/* Page Banner */}
      <section className="relative h-[40vh] min-h-[300px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1553697388-94e804e2f0f6?w=1920&q=80)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/70" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 w-full">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-0.5 bg-orange-500" />
                <span className="text-orange-400 text-sm font-medium tracking-wider uppercase">
                  Get In Touch
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                Contact <span className="text-orange-400">Us</span>
              </h1>
              <p className="text-slate-300 mt-3 max-w-lg">
                Have questions? We're here to help you take the first step
                towards your global education journey.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Floating elements */}
        <motion.div
          animate={{ y: [0, -12, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-20 right-24 z-10 hidden lg:block"
        >
          <div className="w-14 h-14 bg-orange-500/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-orange-500/30">
            <MessageSquare size={24} className="text-orange-400" />
          </div>
        </motion.div>
      </section>

      {/* Contact Info Cards */}
      <section className="relative -mt-16 z-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {contactCards.map((card, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full">
                  <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mb-4">
                    <card.icon size={22} className="text-orange-500" />
                  </div>
                  <h3 className="font-semibold text-slate-800 mb-2">
                    {card.title}
                  </h3>
                  {card.details.map((d, j) => (
                    <p key={j} className="text-slate-500 text-sm">
                      {card.action && j === 0 ? (
                        <a
                          href={card.action}
                          className="hover:text-orange-500 transition-colors"
                          target={card.action.startsWith("http") ? "_blank" : undefined}
                          rel={card.action.startsWith("http") ? "noopener noreferrer" : undefined}
                        >
                          {d}
                        </a>
                      ) : (
                        d
                      )}
                    </p>
                  ))}
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form + Info */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Form */}
            <div className="lg:col-span-3">
              <FadeIn>
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h2 className="text-2xl font-bold text-slate-800 mb-2">
                    Send Us a Message
                  </h2>
                  <p className="text-slate-500 text-sm mb-6">
                    Fill in the form below and our team will get back to you
                    within 24 hours.
                  </p>

                  {submitted ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-12"
                    >
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircle size={32} className="text-green-500" />
                      </div>
                      <h3 className="text-xl font-semibold text-slate-800 mb-2">
                        Thank You for Reaching Out!
                      </h3>
                      <p className="text-slate-500 mb-1">
                        Your message has been sent successfully.
                      </p>
                      <p className="text-slate-400 text-sm">
                        Our team will review your inquiry and get back to you within 24 hours.
                      </p>
                    </motion.div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                            Full Name *
                          </label>
                          <div className="relative">
                            <User
                              size={16}
                              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                            />
                            <Input
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              placeholder="Your full name"
                              required
                              className="pl-10"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                            Email Address *
                          </label>
                          <div className="relative">
                            <Mail
                              size={16}
                              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                            />
                            <Input
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder="your.email@example.com"
                              required
                              className="pl-10"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                            Phone Number
                          </label>
                          <div className="relative">
                            <Phone
                              size={16}
                              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                            />
                            <Input
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              placeholder="+91 XXXXX XXXXX"
                              className="pl-10"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                            Preferred Country
                          </label>
                          <div className="relative">
                            <Globe
                              size={16}
                              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10"
                            />
                            <select
                              name="country"
                              value={formData.country}
                              onChange={handleChange}
                              className="w-full h-10 pl-10 pr-4 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 appearance-none cursor-pointer"
                            >
                              <option value="">Select a country</option>
                              <option value="uk">United Kingdom</option>
                              <option value="usa">United States</option>
                              <option value="canada">Canada</option>
                              <option value="australia">Australia</option>
                              <option value="nz">New Zealand</option>
                              <option value="ireland">Ireland</option>
                              <option value="germany">Germany</option>
                              <option value="france">France</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                          Your Message *
                        </label>
                        <div className="relative">
                          <BookOpen
                            size={16}
                            className="absolute left-3 top-3 text-slate-400"
                          />
                          <Textarea
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            placeholder="Tell us about your study abroad plans..."
                            required
                            rows={5}
                            className="pl-10 resize-none"
                          />
                        </div>
                      </div>

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 h-auto text-base font-medium disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                            </svg>
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send size={16} className="mr-2" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </div>
              </FadeIn>
            </div>

            {/* Side Info */}
            <div className="lg:col-span-2">
              <FadeIn delay={0.2}>
                <div className="bg-slate-900 rounded-2xl p-8 text-white mb-6">
                  <h3 className="text-xl font-bold mb-4">Sun Lumia Overseas</h3>
                  <p className="text-slate-400 text-sm leading-relaxed mb-6">
                    One of Gujarat's leading overseas education consultancies.
                    We've been helping students achieve their study abroad dreams
                    since 2010.
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-orange-500/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <User size={16} className="text-orange-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Director</p>
                        <p className="text-slate-400 text-sm">
                          {companyInfo.director}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-orange-500/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <MapPin size={16} className="text-orange-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Address</p>
                        <p className="text-slate-400 text-sm">
                          {companyInfo.address}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-orange-500/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Phone size={16} className="text-orange-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Phone</p>
                        <p className="text-slate-400 text-sm">
                          {companyInfo.phone}
                        </p>
                        <p className="text-slate-400 text-sm">
                          {companyInfo.altPhone}
                        </p>
                        <p className="text-slate-400 text-sm">
                          {companyInfo.telephone}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* WhatsApp CTA */}
                <a
                  href={`https://wa.me/${companyInfo.whatsapp.replace(/[^0-9]/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-green-500 hover:bg-green-600 text-white rounded-2xl p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <MessageSquare size={24} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg">Chat on WhatsApp</h4>
                      <p className="text-green-100 text-sm">
                        Quick response guaranteed
                      </p>
                    </div>
                  </div>
                </a>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-10 px-4">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="rounded-2xl overflow-hidden shadow-lg h-[400px]">
              <iframe
                title="Sun Lumia Overseas Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3671.123456789!2d72.6369!3d23.2156!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sSector%2011%2C%20Gandhinagar%2C%20Gujarat%20382011!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
